import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { API_BASE } from '../services/apiBase';
import { TourAmendment as TourAmendmentRow, TourDetail as Detail, TourStopDetail } from '../types';
import AmendmentPanel from './AmendmentPanel';
import PackingList from './PackingList';
import { ctrlWheelZoom } from './mapZoom';

/** Boja čiode po stanju stanice — ista podela kao u spisku ispod mape. */
const PIN_KIND: Record<string, string> = {
  'gotova': 'done',
  'na njoj': 'now',
  'izuzetak': 'bad',
  'čeka': 'wait'
};

function pin(state: string, text: string): L.DivIcon {
  return L.divIcon({
    className: `rpin rpin--${PIN_KIND[state] ?? 'wait'}`,
    html: `<span>${text}</span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });
}

/**
 * Mapa ture u toku.
 *
 * Odvojena od mape predloga: tamo se tura sastavlja i klik nešto menja, ovde se
 * gleda šta se već desilo i ništa se ne dira. Spajanje to dvoje značilo bi mapu
 * koja u pola slučajeva ne sme da reaguje na klik.
 */
function TourMap({ stops }: { stops: TourStopDetail[] }) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  const located = stops.filter(
    (s): s is TourStopDetail & { latitude: number; longitude: number } =>
      s.latitude !== null && s.longitude !== null
  );

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false }).setView([45.77, 19.11], 9);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(instance);

    map.current = instance;
    layer.current = L.layerGroup().addTo(instance);

    const odjavi = ctrlWheelZoom(instance, container.current);

    return () => {
      odjavi();
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    const group = layer.current;
    if (!instance || !group) return;

    group.clearLayers();

    const points = located.map((s) => [s.latitude, s.longitude] as [number, number]);

    if (points.length > 1) {
      // Linija ide redom kojim je vozilo STVARNO išlo, jer je vozač smeo da
      // promeni redosled — nacrtan plan bi ovde bio slika ture koja se nije
      // dogodila.
      L.polyline(points, { color: '#17527d', weight: 2, opacity: 0.5, dashArray: '5 6' }).addTo(group);
    }

    located.forEach((stop) => {
      L.marker([stop.latitude, stop.longitude], { icon: pin(stop.state, String(stop.sequence)) })
        .bindTooltip(
          `${stop.sequence}. ${stop.name}${stop.city ? `, ${stop.city}` : ''}`
          + `<br>${stop.state}`
          + (stop.actualArrival ? `<br>stigao ${time(stop.actualArrival)}` : '')
          + (stop.actualDeparture ? ` · otišao ${time(stop.actualDeparture)}` : '')
          + (stop.exceptionReason ? `<br><b>${stop.exceptionReason}</b>` : '')
        )
        .addTo(group);
    });

    instance.invalidateSize();

    if (points.length > 1) {
      instance.fitBounds(L.latLngBounds(points).pad(0.12));
    } else if (points.length === 1) {
      instance.setView(points[0], 13);
    }
  }, [stops]);

  return <div className="tourmap" ref={container} />;
}

interface Props {
  tour: Detail;
  onClose: () => void;
  /** Tura je izmenjena — pozivalac je ponovo učitava. */
  onChanged?: () => void;
}

/**
 * Jedna tura, otvorena.
 *
 * Spisak tura kaže „5/8 stanica“, a to je premalo čim nešto krene naopako:
 * operater tada mora da zna GDE je vozilo stalo, šta je predato i šta ostaje u
 * vozilu. Zato ovde stoji sve zapisano sa terena — pa i planirano vreme uz
 * stvarno, jer je razlika između to dvoje ono što se meri od prvog dana.
 */
export default function TourDetail({ tour, onClose, onChanged }: Props) {
  const [packing, setPacking] = useState(false);
  const [amending, setAmending] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);
  /** Posle skidanja: kupac je dobio „stiže danas" i treba ga pozvati (K13). */
  const [removeNote, setRemoveNote] = useState<string | null>(null);

  // Ta poruka važi za turu na kojoj je nastala, ne i za sledeću otvorenu.
  useEffect(() => { setRemoveNote(null); }, [tour.tourId]);

  /**
   * Stanica za koju operater potvrđuje predaju umesto vozača.
   *
   * Postoji zbog ture 65 od 14.09.2026: vozač je sve razvezao, ali je zatvorio
   * šest od devet stanica. Kad se takva tura zatvori, roba sa neštikliranih
   * VRAĆA SE u planiranje i ide na novu turu — kupac bi je dobio dvaput.
   */
  const [potvrdjujem, setPotvrdjujem] = useState<number | null>(null);
  const [potvrdaRazlog, setPotvrdaRazlog] = useState('');

  /**
   * Ispravka pogrešnog štrika posle zatvaranja ture. Razlog je obavezan —
   * ispravka predaje bez razloga bi bila isto što i brisanje traga.
   */
  const tourZatvorena = tour.status === 'Completed' || tour.status === 'Abandoned';
  const [vracam, setVracam] = useState<{ stopId: number; itemId: number } | null>(null);
  const [vracamRazlog, setVracamRazlog] = useState('');

  const vratiPredaju = async () => {
    if (!vracam || vracamRazlog.trim().length === 0) return;

    try {
      await api.undoDelivery(tour.tourId, vracam.stopId, vracam.itemId, vracamRazlog.trim());
      setVracam(null);
      setVracamRazlog('');
      setRemoveError(null);
      onChanged?.();
    } catch (e) {
      setRemoveError(e instanceof Error ? e.message : String(e));
    }
  };

  const potvrdiPredaju = async (stopId: number) => {
    if (potvrdaRazlog.trim().length === 0) return;

    try {
      await api.confirmDelivered(tour.tourId, stopId, potvrdaRazlog.trim());
      setPotvrdjujem(null);
      setPotvrdaRazlog('');
      setRemoveError(null);
      onChanged?.();
    } catch (e) {
      setRemoveError(e instanceof Error ? e.message : String(e));
    }
  };
  const withoutPoint = tour.stops.filter((s) => s.latitude === null || s.longitude === null);

  // Izmena ima smisla samo dok tura još može da se vozi. Završenoj i odbačenoj
  // dodavanje tačke ništa ne menja, pa se dugme i ne nudi.
  const canBeAmended = tour.status === 'Planned' || tour.status === 'InProgress';

  /**
   * Preuzimanja koja ne stoje tamo gde je roba.
   *
   * Mesto preuzimanja se računa kad se tura slaže i posle se ne menja samo.
   * Tura 164, 12.10.2026: preuzimanje u SO2, a roba je od 06:51 bila odvojena
   * u magacinu 021 — vozač je otišao po nju na pogrešno mesto. Čita se iz
   * naloga za pakovanje, jer on već zna gde roba stoji.
   */
  const [sukobi, setSukobi] = useState<string[]>([]);

  useEffect(() => {
    if (!canBeAmended) {
      setSukobi([]);
      return;
    }

    let aktivno = true;
    api.packingList(tour.tourId)
      .then((list) => { if (aktivno) setSukobi(list.conflicts ?? []); })
      // Upozorenje je dodatak; ako nalog ne može da se pročita, pregled ture
      // i dalje radi.
      .catch(() => { if (aktivno) setSukobi([]); });

    return () => { aktivno = false; };
  }, [tour.tourId, canBeAmended, tour.stops.length]);

  const remove = async (stopId: number) => {
    if (removeReason.trim().length === 0) return;

    try {
      const ishod = await api.removeStop(tour.tourId, stopId, removeReason.trim());
      setRemoving(null);
      setRemoveReason('');
      setRemoveError(null);
      // Kupac je dobio „stiže danas" — inače bi čekao ceo dan (K13).
      setRemoveNote(ishod?.kupacNajavljen
        ? `Kupac sa stanice „${ishod.stanica}" je dobio poruku „stiže danas" — pozovi ga i reci da danas ne stiže.`
        : null);
      onChanged?.();
    } catch (e) {
      setRemoveError(e instanceof Error ? e.message : String(e));
    }
  };

  if (packing) {
    return <PackingList tourId={tour.tourId} onClose={() => setPacking(false)} />;
  }

  if (amending) {
    return (
      <AmendmentPanel
        tour={tour}
        onClose={() => setAmending(false)}
        onChanged={() => onChanged?.()}
      />
    );
  }

  return (
    <div className="tourdetail">
      <div className="tourdetail__head">
        <h3>
          {tour.name}
          <span className="muted">
            {' '}· {tour.driver ?? 'bez vozača'} · {tour.vehicle ?? 'bez vozila'}
          </span>
        </h3>
        <span className="tourdetail__actions">
          <button type="button" onClick={() => setPacking(true)}>Nalog za pakovanje</button>
          {canBeAmended && (
            <button type="button" onClick={() => setAmending(true)}>Dodaj tačku</button>
          )}
          <button type="button" className="link" onClick={onClose}>Zatvori</button>
        </span>
      </div>

      <p className="muted">
        {tour.actualStart ? `Krenula ${time(tour.actualStart)}` : 'Još nije krenula'}
        {tour.actualEnd && ` · završena ${time(tour.actualEnd)}`}
        {tour.lastSyncAt && ` · poslednje javljanje ${time(tour.lastSyncAt)}`}
      </p>

      {removeError && <p className="blocker">{removeError}</p>}
      {removeNote && <div className="health health--bad">{removeNote}</div>}

      {sukobi.length > 0 && (
        <div className="blocker">
          {/* Preuzimanje se na sačuvanu turu ne dodaje kroz ekran — rečenica kaže
              put koji postoji (analiza scenarija, K16). */}
          <strong>Preuzimanje se ne slaže sa mestom robe.</strong> Ako tura još nije
          krenula, odbaci je i složi ponovo — predlog doda preuzimanje tamo gde roba
          stoji. Ako je krenula, javi vozaču gde roba stoji.
          <ul>
            {sukobi.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}

      {(tour.amendments?.length ?? 0) > 0 && (
        <Amendments list={tour.amendments!} onChanged={() => onChanged?.()} />
      )}

      <TourMap stops={tour.stops} />

      {withoutPoint.length > 0 && (
        <p className="note">
          Bez tačke na mapi: {withoutPoint.map((s) => s.name).join(', ')}. Stanica se
          vidi u spisku, ali se ne crta — adresa nema potvrđenu koordinatu.
        </p>
      )}

      <ul className="tourstops">
        {tour.stops.map((stop) => (
          <li key={stop.stopId} className={`tourstops__row tourstops__row--${PIN_KIND[stop.state] ?? 'wait'}`}>
            <span className="tourstops__no">{stop.sequence}</span>

            <div className="tourstops__body">
              <p className="tourstops__where">
                {stop.name}
                {stop.city ? `, ${stop.city}` : ''}
                {stop.isPickup && <span className="flag"> roba se uzima</span>}
                {stop.requiredTwoPeople && <span className="flag"> dva čoveka</span>}
                {stop.hasSignature && <span className="flag flag--ok"> potpisano</span>}
              </p>

              <p className="muted tourstops__times">
                {stop.actualArrival
                  ? `stigao ${time(stop.actualArrival)}`
                  : stop.gpsArrival
                    // Vozač nije štiklirao, ali vozilo JESTE bilo tu — bez
                    // ovoga bi stanica izgledala kao da niko nije došao.
                    ? `vozilo stiglo ${time(stop.gpsArrival)}`
                    : stop.plannedArrival
                      ? `planirano ${time(stop.plannedArrival)}`
                      : 'bez vremena'}
                {stop.actualDeparture
                  ? ` · otišao ${time(stop.actualDeparture)}`
                  : stop.gpsDeparture && ` · vozilo otišlo ${time(stop.gpsDeparture)}`}
                {/* Kad postoje oba, razlika govori koliko se kasni sa
                    štikliranjem — to je podatak o navici, ne o vožnji. */}
                {stop.actualArrival && stop.gpsArrival
                  && Math.abs(new Date(stop.actualArrival).getTime()
                    - new Date(stop.gpsArrival).getTime()) > 10 * 60_000 && (
                  <span className="muted"> · GPS {time(stop.gpsArrival)}</span>
                )}
                {/* Razlika plan–stvarno je ono zbog čega se vremena i beleže.
                    Bez nje bi ekran imao dva broja koja niko ne poredi. */}
                {stop.plannedArrival && stop.actualArrival && (
                  <span className="tourstops__delta"> · {lateness(stop.plannedArrival, stop.actualArrival)}</span>
                )}
              </p>

              {stop.items.length > 0 && (
                <p className="tourstops__items">
                  {stop.items.map((item) => (
                    <span
                      key={`${stop.stopId}-${item.articleCode}-${item.articleName}`}
                      className={item.isDelivered ? 'done' : 'pending'}
                    >
                      {item.isDelivered ? '✓ ' : '· '}
                      {item.articleName} × {item.quantity}
                      {/* Pogrešan štrik posle zatvaranja ture ispravlja operater
                          (odluka vlasnika 14.10.2026); dok je tura otvorena —
                          vozač, na telefonu. */}
                      {item.isDelivered && tourZatvorena && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="link"
                            onClick={() => { setVracam({ stopId: stop.stopId, itemId: item.itemId }); setVracamRazlog(''); }}
                          >
                            nije predato
                          </button>
                        </>
                      )}
                    </span>
                  ))}
                </p>
              )}

              {vracam?.stopId === stop.stopId && (
                <p className="tourstops__skidanje">
                  <input
                    type="text"
                    value={vracamRazlog}
                    onChange={(e) => setVracamRazlog(e.target.value)}
                    placeholder="Zašto nije predato? npr. vozač greškom štiklirao, kupac potvrdio telefonom"
                  />
                  <button
                    type="button"
                    disabled={vracamRazlog.trim().length === 0}
                    onClick={() => void vratiPredaju()}
                  >
                    Vrati — nije predato
                  </button>
                  <button type="button" className="link" onClick={() => setVracam(null)}>Odustani</button>
                </p>
              )}

              {stop.removedAt ? (
                <p className="tourstops__skinuto">
                  Skinuta sa ture{stop.removedReason ? `: ${stop.removedReason}` : '.'}{' '}
                  Roba ostaje u vozilu dok se ne odluči šta s njom.
                </p>
              ) : canBeAmended && !stop.actualArrival && (
                removing === stop.stopId ? (
                  <p className="tourstops__skidanje">
                    <input
                      type="text"
                      value={removeReason}
                      placeholder="Zašto se skida (npr. kupac otkazao)"
                      onChange={(e) => setRemoveReason(e.target.value)}
                    />
                    <button
                      type="button"
                      className="primary"
                      disabled={removeReason.trim().length === 0}
                      onClick={() => void remove(stop.stopId)}
                    >
                      Skini
                    </button>
                    <button type="button" className="link" onClick={() => setRemoving(null)}>
                      odustani
                    </button>
                  </p>
                ) : (
                  <p>
                    <button
                      type="button"
                      className="link link--danger"
                      onClick={() => { setRemoving(stop.stopId); setRemoveReason(''); }}
                    >
                      skini sa ture
                    </button>
                  </p>
                )
              )}

              {/* Roba koja nije štiklirana, a stanica nije skinuta. Bez ovoga
                  se po zatvaranju ture vraća u planiranje. */}
              {!stop.removedAt && !stop.deliveryConfirmedBy
                && stop.items.some((i) => !i.isDelivered) && (
                potvrdjujem === stop.stopId ? (
                  <p className="tourstops__skidanje">
                    <input
                      type="text"
                      value={potvrdaRazlog}
                      placeholder="Odakle znaš da je predato (npr. vozač potvrdio telefonom)"
                      onChange={(e) => setPotvrdaRazlog(e.target.value)}
                    />
                    <button
                      type="button"
                      className="primary"
                      disabled={potvrdaRazlog.trim().length === 0}
                      onClick={() => void potvrdiPredaju(stop.stopId)}
                    >
                      Potvrdi predaju
                    </button>
                    <button
                      type="button"
                      className="link"
                      onClick={() => setPotvrdjujem(null)}
                    >
                      odustani
                    </button>
                  </p>
                ) : (
                  <p>
                    <button
                      type="button"
                      className="link"
                      onClick={() => { setPotvrdjujem(stop.stopId); setPotvrdaRazlog(''); }}
                    >
                      predato je, vozač nije štiklirao
                    </button>
                  </p>
                )
              )}

              {stop.deliveryConfirmedBy && (
                <p className="tourstops__potvrdjeno">
                  Predaju potvrdio {stop.deliveryConfirmedBy}
                  {stop.deliveryConfirmedNote ? `: ${stop.deliveryConfirmedNote}` : '.'}
                  {' '}Vreme ostaje vozačevo — nije izmereno ovom potvrdom.
                </p>
              )}

              {stop.exceptionReason && (
                <p className="tourstops__exception">
                  {stop.exceptionReason}
                  {stop.exceptionResolution
                    ? ` — odlučeno: ${stop.exceptionResolution}`
                    : ' — čeka odluku'}
                </p>
              )}

              {/* Zašto je vozač promenio redosled. To je znanje o terenu i zbog
                  toga se i traži jednim tapom — ne sme da ostane samo u bazi. */}
              {stop.reorderReason && (
                <p className="muted">Redosled promenjen: {stop.reorderReason}</p>
              )}

              {stop.notes && <p className="muted">Beleška: {stop.notes}</p>}

              {/* Fotografije sa stanice. Klik otvara punu sliku u novom prozoru. */}
              {(stop.photos ?? []).length > 0 && (
                <div className="stop-photos">
                  {(stop.photos ?? []).map((foto) => {
                    const url = `${API_BASE}/api/monitor/tours/${tour.tourId}/fotografije/${foto.id}`;
                    const kad = new Date(foto.takenAt).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <a key={foto.id} href={url} target="_blank" rel="noreferrer"
                        title={`${kad}${foto.note ? ` — ${foto.note}` : ''}`}>
                        <img src={url} alt={foto.note ?? `Fotografija u ${kad}`} loading="lazy" />
                      </a>
                    );
                  })}
                </div>
              )}

              {stop.phones.length > 0 && (
                <p className="tourstops__phones">
                  {stop.phones.map((number) => (
                    <a key={number} href={`tel:${number}`}>{number}</a>
                  ))}
                </p>
              )}
            </div>

            <span className={`flag flag--${flagOf(stop.state)}`}>{stop.state}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Izmene napravljene pošto je tura puštena.
 *
 * Jedini red koji ovde stvarno nešto menja je „vozač još nije preuzeo": dok
 * tako piše, operater mora računati da vozač ne zna za tačku. Telefon ume da
 * bude bez signala satima, pa se to ne pretpostavlja — kad se ne može čekati,
 * operater zove i to zabeleži.
 */
function Amendments({ list, onChanged }: { list: TourAmendmentRow[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<number | null>(null);

  const told = async (id: number) => {
    setBusy(id);
    try {
      await api.markAmendmentToldByPhone(id);
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="amendlog">
      <p className="amendlog__title">Izmene posle puštanja <span className="muted">· {list.length}</span></p>

      <ul>
        {list.map((a) => (
          <li key={a.id} className={a.acknowledgedAt ? 'amendlog__row' : 'amendlog__row amendlog__row--nezna'}>
            <p className="amendlog__what">
              <strong>{a.stopLabel}</strong>
              <span className="muted">
                {' '}· {a.createdBy}, {time(a.createdAt)}
                {a.addedKm !== null && ` · +${a.addedKm.toFixed(1)} km`}
                {a.addedMinutes !== null && ` · +${Math.round(a.addedMinutes)} min`}
              </span>
            </p>

            <p className="muted">Razlog: {a.reason}</p>

            {a.overriddenWarnings && (
              <p className="amendlog__warn">Pregaženo: {a.overriddenWarnings}</p>
            )}

            {a.acknowledgedAt ? (
              <p className="amendlog__ok">
                Vozač preuzeo {time(a.acknowledgedAt)}
                {a.acknowledgedVia ? ` · ${a.acknowledgedVia}` : ''}
              </p>
            ) : (
              <p className="amendlog__nezna">
                Vozač još nije preuzeo — računaj da ne zna.{' '}
                <button
                  type="button"
                  className="link"
                  disabled={busy === a.id}
                  onClick={() => void told(a.id)}
                >
                  javio sam mu telefonom
                </button>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function flagOf(state: string): string {
  if (state === 'gotova') return 'ok';
  if (state === 'izuzetak') return 'bad';
  // „Vozilo prošlo" nije isto što i „gotova": roba možda nije predata, ali
  // se zna da je vozilo bilo tu — nije neutralno stanje ni upozorenje.
  if (state === 'vozilo prošlo' || state === 'vozilo tu') return '';
  return 'warn';
}

function time(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

/** „18 min kasnije“ ili „6 min ranije“ — minuti u odnosu na plan. */
function lateness(planned: string, actual: string): string {
  const minutes = Math.round(
    (new Date(actual).getTime() - new Date(planned).getTime()) / 60000
  );

  if (!Number.isFinite(minutes) || minutes === 0) return 'u minut';
  return minutes > 0 ? `${minutes} min kasnije` : `${-minutes} min ranije`;
}
